import { postStripeAccountStatus, getSellerViewStats, postGetAllWarehouses } from '../../util/api';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { fetchCurrentUser, fetchNotificationData } from '../../ducks/user.duck';
import { fetchSummary } from '../SellerFinancePage/SellerFinancePage.duck';
import { isSellerFinanceEnabledFor } from '../SellerFinancePage/gating';
import { fetchEconomyOrders, currentPeriod } from '../SellerEconomyPage/SellerEconomyPage.duck';
import { LOW_STOCK_THRESHOLD, isRunningOut } from '../ManageListingsPage/listingCounters';
import { isCatalogueRow } from '../../util/catalogueCore';

/**
 * State for the seller overview page.
 *
 * The client's note on P25009-385 is right that this page is "an aggregation, not
 * new data". Two of the four headline numbers need no request at all:
 * `state.user.notifications` is already fetched for the topbar bell and carries
 * both the new-order and unread-message counts. This duck fetches only what is
 * genuinely missing:
 *
 *   - Stripe account status  -> whether payouts are blocked
 *   - 7-day view stats       -> the new listing-view events
 *   - own listings           -> draft / low-stock / published counts
 *   - saved addresses        -> one Kom igång step
 *   - finance summary        -> available balance (delegated to SellerFinancePage)
 *
 * Every fetch fails independently. A seller whose Stripe status request times out
 * should still see their orders and messages, so no rejection is allowed to take
 * the page down with it — hence the `.catch` on each branch and a per-section
 * error rather than one page-level one.
 */

// ================ Action types ================ //

export const FETCH_DASHBOARD_REQUEST = 'app/SellerDashboardPage/FETCH_DASHBOARD_REQUEST';
export const FETCH_DASHBOARD_SUCCESS = 'app/SellerDashboardPage/FETCH_DASHBOARD_SUCCESS';

export const FETCH_STRIPE_STATUS_SUCCESS = 'app/SellerDashboardPage/FETCH_STRIPE_STATUS_SUCCESS';
export const FETCH_STRIPE_STATUS_ERROR = 'app/SellerDashboardPage/FETCH_STRIPE_STATUS_ERROR';

export const FETCH_VIEW_STATS_SUCCESS = 'app/SellerDashboardPage/FETCH_VIEW_STATS_SUCCESS';
export const FETCH_VIEW_STATS_ERROR = 'app/SellerDashboardPage/FETCH_VIEW_STATS_ERROR';

export const FETCH_LISTING_COUNTS_SUCCESS =
  'app/SellerDashboardPage/FETCH_LISTING_COUNTS_SUCCESS';
export const FETCH_LISTING_COUNTS_ERROR = 'app/SellerDashboardPage/FETCH_LISTING_COUNTS_ERROR';

export const FETCH_ADDRESS_COUNT_SUCCESS = 'app/SellerDashboardPage/FETCH_ADDRESS_COUNT_SUCCESS';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,

  stripeStatus: null,
  stripeStatusError: null,

  viewStats: null,
  viewStatsError: null,

  listingCounts: null, // { published, drafts, lowStock, catalogue }
  listingCountsError: null,

  addressCount: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_DASHBOARD_REQUEST:
      /* Clear previous errors so a retry starts from a clean slate; keep the
       * previous values so the page does not flash empty while refreshing. */
      return {
        ...state,
        fetchInProgress: true,
        stripeStatusError: null,
        viewStatsError: null,
        listingCountsError: null,
      };
    case FETCH_DASHBOARD_SUCCESS:
      return { ...state, fetchInProgress: false };

    case FETCH_STRIPE_STATUS_SUCCESS:
      return { ...state, stripeStatus: payload, stripeStatusError: null };
    case FETCH_STRIPE_STATUS_ERROR:
      return { ...state, stripeStatusError: payload };

    case FETCH_VIEW_STATS_SUCCESS:
      return { ...state, viewStats: payload, viewStatsError: null };
    case FETCH_VIEW_STATS_ERROR:
      return { ...state, viewStatsError: payload };

    case FETCH_LISTING_COUNTS_SUCCESS:
      return { ...state, listingCounts: payload, listingCountsError: null };
    case FETCH_LISTING_COUNTS_ERROR:
      return { ...state, listingCountsError: payload };

    case FETCH_ADDRESS_COUNT_SUCCESS:
      return { ...state, addressCount: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchDashboardRequest = () => ({ type: FETCH_DASHBOARD_REQUEST });
export const fetchDashboardSuccess = () => ({ type: FETCH_DASHBOARD_SUCCESS });

// ================ Thunks ================ //

/** Stripe onboarding state — drives the "payouts are blocked" to-do row. */
export const fetchStripeStatus = () => dispatch =>
  postStripeAccountStatus()
    .then(res => {
      /* The endpoint answers `{ account: null }` for a seller with no connected
       * account, which is not an error — it just means there is nothing to
       * report. Normalising to null keeps actionCenter's "status not loaded yet"
       * and "no account" cases distinct via stripeConnected. */
      dispatch({ type: FETCH_STRIPE_STATUS_SUCCESS, payload: res?.data?.account ?? null });
    })
    .catch(e => {
      dispatch({ type: FETCH_STRIPE_STATUS_ERROR, payload: storableError(e) });
    });

/** 7-day views and trend from the listing-view events. */
export const fetchViewStats = () => dispatch =>
  getSellerViewStats()
    .then(res => {
      dispatch({ type: FETCH_VIEW_STATS_SUCCESS, payload: res });
    })
    .catch(e => {
      dispatch({ type: FETCH_VIEW_STATS_ERROR, payload: storableError(e) });
    });

/* Re-exported so existing importers keep working. The value and the rule that
 * uses it now live in ManageListingsPage/listingCounters.js, because the listings
 * page needs the same answer and two copies produced two different numbers. */
export { LOW_STOCK_THRESHOLD };

/**
 * Draft, published and low-stock counts in one pass.
 *
 * Sharetribe cannot filter own listings by stock level, so an exact low-stock
 * count means looking at every listing. This paginates rather than sampling: a
 * "3 listings are running out" figure that silently only covered the first page
 * would be worse than no figure.
 */
export const fetchListingCounts = () => async (dispatch, getState, sdk) => {
  try {
    const collected = [];
    let page = 1;
    let totalPages = 1;
    /* Same ceiling as the CSV export uses, so a pathological account cannot make
     * the dashboard hang forever. */
    const MAX_PAGES = 20;

    while (page <= totalPages && page <= MAX_PAGES) {
      // eslint-disable-next-line no-await-in-loop
      const response = await sdk.ownListings.query({
        page,
        perPage: 100,
        include: ['currentStock'],
        pub_isDeleted: false,
      });
      collected.push(...denormalisedResponseEntities(response));
      totalPages = response?.data?.meta?.totalPages || 1;
      page += 1;
    }

    /* Storage-module items are drafts by design and belong on /forvaring, not in
     * the seller's draft count — the same exclusion ManageListingsPage makes. */
    const listings = collected.filter(
      l => l?.attributes?.publicData?.listingMode !== 'storage'
    );

    /* Ads only: a catalogue row is published too (P25009-410), but it is not
     * on the marketplace and is counted on its own below. */
    const published = listings.filter(
      l => l?.attributes?.state === 'published' && !isCatalogueRow(l?.attributes)
    ).length;
    const drafts = listings.filter(l => l?.attributes?.state === 'draft').length;
    /* P25009-410 — rows in the catalogue. A workshop that connected its stock
     * and chose to advertise nothing has done the "Anslut ditt lager" step. */
    const catalogue = listings.filter(l => isCatalogueRow(l?.attributes)).length;

    /* Shared rule — see `isRunningOut` in ManageListingsPage/listingCounters.js.
     *
     * This check used to be written out here as "state === 'published' and stock
     * <= threshold", omitting `isHide`, and the two surfaces disagreed in public:
     * on the test account the dashboard row said "9 annonser tar slut efter nästa
     * order" while the listings page said 3. All six extra rows were hidden
     * listings, which are off the marketplace and cannot run out. */
    const lowStock = listings.filter(l =>
      isRunningOut({
        state: l?.attributes?.state,
        isHide: l?.attributes?.publicData?.isHide,
        isDeleted: l?.attributes?.publicData?.isDeleted,
        quantity: l?.currentStock?.attributes?.quantity,
      })
    ).length;

    dispatch({
      type: FETCH_LISTING_COUNTS_SUCCESS,
      payload: { published, drafts, lowStock, catalogue, truncated: totalPages > MAX_PAGES },
    });
  } catch (e) {
    dispatch({ type: FETCH_LISTING_COUNTS_ERROR, payload: storableError(e) });
  }
};

/** Saved pickup/return addresses — one Kom igång step. */
export const fetchAddressCount = () => dispatch =>
  postGetAllWarehouses({})
    .then(res => {
      const list = res?.data || res?.warehouses || res || [];
      dispatch({
        type: FETCH_ADDRESS_COUNT_SUCCESS,
        payload: Array.isArray(list) ? list.length : 0,
      });
    })
    .catch(() => {
      /* Silent: a missing address count only affects one checklist tick, and an
       * error message about it would be noise on a page about orders and money. */
      dispatch({ type: FETCH_ADDRESS_COUNT_SUCCESS, payload: null });
    });

export const loadData = () => (dispatch, getState) => {
  dispatch(fetchDashboardRequest());

  const stripeAccountId = getState().user?.currentUser?.stripeAccount?.attributes
    ?.stripeAccountId;

  /* The balance card comes from the Ekonomi summary, which is behind the same
   * flag and pilot allowlist as the Ekonomi page itself. Asking for it when the
   * flag is off would 4xx on every dashboard load; the card renders as
   * unavailable instead. */
  const balanceMaybe = isSellerFinanceEnabledFor(stripeAccountId)
    ? dispatch(fetchSummary()).catch(() => null)
    : Promise.resolve(null);

  return Promise.all([
    /* Refreshes stripeConnected, which the payout to-do row reads. */
    dispatch(fetchCurrentUser()).catch(() => null),

    /* Two of the four headline cards come from here, so the page cannot report
     * itself loaded until this resolves.
     *
     * fetchCurrentUser does dispatch this internally, but inside a .then() that
     * it never returns — so awaiting fetchCurrentUser does NOT wait for the
     * notifications. The counts then landed after the skeleton had already been
     * replaced, which looks like a stale number rather than a loading one: open
     * the page, see yesterday's order count, and nothing tells you it is about
     * to change. Dispatching it explicitly costs one duplicate query and makes
     * the load honest. Deduplicating it belongs with the notification rework in
     * P25009-390, which owns this fetch. */
    dispatch(fetchNotificationData()).catch(() => null),
    dispatch(fetchStripeStatus()),
    dispatch(fetchViewStats()),
    dispatch(fetchListingCounts()),
    dispatch(fetchAddressCount()),
    balanceMaybe,
    /* The money card shows this month's "Till dig" — the Ekonomi page's own
     * figure for the month, from its own rows (P25009-496), so the two can
     * never disagree. Never rejects: a failure is stored on the Ekonomi state
     * and the card says the figure is unavailable. */
    dispatch(fetchEconomyOrders(currentPeriod())),
  ]).then(() => {
    dispatch(fetchDashboardSuccess());
  });
};
